/**
 * StatusBar - Bottom status bar showing connection and terminal info
 */

interface Terminal {
  id: string;
  name: string;
  current_disk: string | null;
  current_cwd: string;
}

interface BrainStatus {
  state: string;
  exists: boolean;
  brain_id?: string;
  relay_connected?: boolean;
}

interface StatusBarProps {
  brainStatus: BrainStatus;
  activeTerminal: Terminal | undefined;
}

export default function StatusBar({ brainStatus, activeTerminal }: StatusBarProps) {
  const getBrainStateColor = () => {
    switch (brainStatus.state) {
      case 'active':
        return 'text-green-400';
      case 'starting':
      case 'connecting':
        return 'text-yellow-400';
      case 'error':
        return 'text-red-400';
      default:
        return 'text-gray-400';
    }
  };

  const getBrainStateIcon = () => {
    switch (brainStatus.state) {
      case 'active':
        return (
          <svg className="w-3 h-3 fill-current" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" />
          </svg>
        );
      case 'starting':
      case 'connecting':
        return (
          <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
        );
      default:
        return (
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" strokeWidth="2" />
          </svg>
        );
    }
  };

  return (
    <footer className="flex-shrink-0 bg-gray-800 border-t border-gray-700 px-4 py-1.5 flex items-center justify-between text-xs">
      {/* Left side - Brain status */}
      <div className="flex items-center gap-4">
        <div className={`flex items-center gap-1.5 ${getBrainStateColor()}`}>
          {getBrainStateIcon()}
          <span>Brain: {brainStatus.state}</span>
        </div>

        {brainStatus.relay_connected && (
          <div className="flex items-center gap-1.5 text-green-400">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span>Relay connected</span>
          </div>
        )}
      </div>

      {/* Right side - Terminal info */}
      <div className="flex items-center gap-4 text-gray-400">
        {activeTerminal && (
          <>
            <span>
              Disk: {activeTerminal.current_disk || 'none'}
            </span>
            <span>
              CWD: {activeTerminal.current_cwd}
            </span>
          </>
        )}
        <span>VoxTerm v0.1.0</span>
      </div>
    </footer>
  );
}
